// PickupHistoryCard — read-only parent-facing pickup audit log.
//
// Calm, last-N events grouped by date. Renders "Picked up by {Name}"
// for authorized pickups, "Picked up by parent/guardian" for self,
// or "Picked up at HH:MM" for legacy rows with no audit.

import { useEffect, useState } from "react";
import { CalendarCheck, House } from "@phosphor-icons/react";
import { listFamilyPickupHistory } from "@/lib/attendance";
import PickupPhoto from "@/components/pickup/PickupPhoto";
import ChildAvatar from "@/components/children/ChildAvatar";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PickupHistoryCard({ children = [], limit = 20 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const childIds = children.map((c) => c.id).filter(Boolean);
    if (childIds.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    listFamilyPickupHistory({ childIds, limit })
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch(() => {
        if (alive) setRows([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [children, limit]);

  return (
    <section
      data-testid="parent-pickup-history"
      className="rounded-2xl border border-neutral-200 bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
          <CalendarCheck size={16} weight="duotone" />
        </span>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Pickup history
          </p>
          <p className="text-sm font-semibold text-neutral-900">
            Recent check-outs at the door
          </p>
        </div>
      </div>

      {loading && (
        <p className="mt-3 text-sm text-neutral-500">Loading…</p>
      )}

      {!loading && rows.length === 0 && (
        <p
          data-testid="parent-pickup-history-empty"
          className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-3 text-center text-sm text-neutral-500"
        >
          No pickups recorded yet. Once a teacher stamps a pickup at the
          door, it'll show up here.
        </p>
      )}

      {!loading && rows.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="parent-pickup-history-list">
          {rows.map((r) => {
            const who = r.pickup_authorization?.full_name
              ? r.pickup_authorization.full_name
              : r.pickup_self
                ? "Parent / guardian"
                : null;
            const childName =
              [r.child?.first_name, r.child?.last_name].filter(Boolean).join(" ");
            // Authorized → reuse the auth person's photo.
            // Self → reuse the child's profile photo.
            // Legacy (no audit) → small calendar icon swatch.
            let avatar;
            if (r.pickup_authorization?.full_name) {
              avatar = (
                <PickupPhoto
                  path={r.pickup_authorization.photo_path}
                  name={r.pickup_authorization.full_name}
                  size={36}
                />
              );
            } else if (r.pickup_self) {
              avatar = (
                <ChildAvatar
                  path={r.child?.profile_photo_path}
                  name={childName}
                  size={36}
                />
              );
            } else {
              avatar = (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
                  <CalendarCheck size={16} weight="duotone" />
                </span>
              );
            }
            return (
              <li
                key={r.id}
                data-testid={`parent-pickup-history-row-${r.id}`}
                data-pickup-by={r.pickup_authorization?.full_name ? "authorized" : r.pickup_self ? "self" : "legacy"}
                className="flex items-center gap-3 rounded-2xl border border-neutral-100 bg-neutral-50/40 p-3"
              >
                {avatar}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-neutral-900">
                    {childName}
                  </p>
                  <p className="text-[11px] text-neutral-600">
                    {formatDate(r.date)} · {formatTime(r.check_out_at)}
                    {who ? ` · by ${who}` : ""}
                  </p>
                </div>
                {r.pickup_self && (
                  <House
                    size={12}
                    weight="duotone"
                    aria-hidden
                    className="shrink-0 text-primary-hover"
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
